// src/services/RobustnessUtils.ts
/**
 * Robustness Utilities - Platform Consolidation
 * Provides retry logic, circuit breaker, timeout, and caching mechanisms
 */

import { ErrorLoggerService } from './ErrorLoggerService';

// ==================== TYPES ====================

export interface RetryOptions {
  maxAttempts?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  maxDelayMs?: number;
  /** Add random jitter (0-30% of delay) to avoid thundering herd. Default: true */
  jitter?: boolean;
  /** Return false to abort retries immediately (e.g. 4xx client errors) */
  isRetryable?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
}

export interface TimeoutOptions {
  timeoutMs: number;
  timeoutError?: string;
}

export interface CircuitBreakerOptions {
  failureThreshold?: number;
  resetTimeoutMs?: number;
  monitoringPeriodMs?: number;
  /** Consecutive successes required in HALF_OPEN before closing (default: 2) */
  halfOpenSuccessThreshold?: number;
}

export interface CacheOptions {
  ttlMs: number;
  key: string;
}

// ==================== RETRY LOGIC ====================

/**
 * Retry a promise-returning function with exponential backoff
 */
/**
 * Default retryability check: skip retries for client-side HTTP errors (4xx).
 * Supabase wraps errors as objects with a `status` field.
 */
function defaultIsRetryable(error: any): boolean {
  const status = error?.status ?? error?.statusCode ?? error?.code;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return false; // 4xx: authentication/authorization/bad-request — not transient
  }
  return true;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    delayMs = 1000,
    backoffMultiplier = 2,
    maxDelayMs = 10000,
    jitter = true,
    isRetryable = defaultIsRetryable,
    onRetry
  } = options;

  let lastError: any;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Abort immediately for non-retryable errors
      if (!isRetryable(error)) {
        console.warn(`[Retry] Non-retryable error on attempt ${attempt}, aborting.`, error);
        break;
      }

      // Don't retry on last attempt
      if (attempt === maxAttempts) {
        break;
      }

      // Exponential backoff with optional jitter
      let currentDelay = Math.min(
        delayMs * Math.pow(backoffMultiplier, attempt - 1),
        maxDelayMs
      );
      if (jitter) {
        currentDelay = currentDelay * (1 + Math.random() * 0.3);
      }

      console.warn(`[Retry] Attempt ${attempt}/${maxAttempts} failed, retrying in ${Math.round(currentDelay)}ms...`, error);

      if (onRetry) {
        onRetry(attempt, error);
      }

      await sleep(currentDelay);
    }
  }

  // All attempts failed
  console.error(`[Retry] All ${maxAttempts} attempts failed`, lastError);
  throw lastError;
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ==================== TIMEOUT ====================

/**
 * Add timeout to any promise
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  options: TimeoutOptions
): Promise<T> {
  const { timeoutMs, timeoutError = 'Operation timed out' } = options;

  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutError)), timeoutMs)
    )
  ]);
}

// ==================== CIRCUIT BREAKER ====================

enum CircuitState {
  CLOSED = 'CLOSED',     // Normal operation
  OPEN = 'OPEN',         // Failing, reject immediately
  HALF_OPEN = 'HALF_OPEN' // Testing if service recovered
}

class CircuitBreaker {
  private state: CircuitState = CircuitState.CLOSED;
  private failureCount: number = 0;
  private halfOpenSuccessCount: number = 0;
  private lastFailureTime: number = 0;
  private nextAttemptTime: number = 0;

  constructor(
    private name: string,
    private options: CircuitBreakerOptions = {}
  ) {
    this.options = {
      failureThreshold: options.failureThreshold ?? 5,
      resetTimeoutMs: options.resetTimeoutMs ?? 60000, // 1 minute
      monitoringPeriodMs: options.monitoringPeriodMs ?? 10000, // 10 seconds
      halfOpenSuccessThreshold: options.halfOpenSuccessThreshold ?? 2
    };
  }

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check circuit state
    if (this.state === CircuitState.OPEN) {
      const now = Date.now();

      // Check if we should try again
      if (now >= this.nextAttemptTime) {
        console.log(`[CircuitBreaker:${this.name}] Trying HALF_OPEN state`);
        this.state = CircuitState.HALF_OPEN;
      } else {
        const waitMs = this.nextAttemptTime - now;
        throw new Error(`Circuit breaker is OPEN. Retry in ${Math.ceil(waitMs / 1000)}s`);
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    if (this.state === CircuitState.HALF_OPEN) {
      this.halfOpenSuccessCount++;
      if (this.halfOpenSuccessCount >= this.options.halfOpenSuccessThreshold!) {
        console.log(`[CircuitBreaker:${this.name}] Recovery confirmed (${this.halfOpenSuccessCount} successes), closing circuit`);
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.halfOpenSuccessCount = 0;
      }
    } else {
      this.failureCount = 0;
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.halfOpenSuccessCount = 0;
    this.lastFailureTime = Date.now();

    if (this.state === CircuitState.HALF_OPEN) {
      console.warn(`[CircuitBreaker:${this.name}] HALF_OPEN test failed, reopening circuit`);
      this.openCircuit();
    } else if (this.failureCount >= this.options.failureThreshold!) {
      console.error(`[CircuitBreaker:${this.name}] Failure threshold reached, opening circuit`);
      this.openCircuit();
    }
  }

  private openCircuit(): void {
    this.state = CircuitState.OPEN;
    this.nextAttemptTime = Date.now() + this.options.resetTimeoutMs!;

    ErrorLoggerService.logError({
      errorType: 'runtime',
      errorMessage: `Circuit breaker opened for ${this.name}`,
      severity: 'critical',
      componentName: 'CircuitBreaker',
      metadata: {
        circuitName: this.name,
        failureCount: this.failureCount,
        resetTimeoutMs: this.options.resetTimeoutMs
      }
    });
  }

  getState(): CircuitState {
    return this.state;
  }

  reset(): void {
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.halfOpenSuccessCount = 0;
  }
}

// Circuit breaker instances for different services
const circuitBreakers = new Map<string, CircuitBreaker>();

export function getCircuitBreaker(name: string, options?: CircuitBreakerOptions): CircuitBreaker {
  if (!circuitBreakers.has(name)) {
    circuitBreakers.set(name, new CircuitBreaker(name, options));
  }
  return circuitBreakers.get(name)!;
}

// ==================== CACHING ====================

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly maxSize: number;
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;

  constructor(maxSize = 500) {
    this.maxSize = maxSize;
    // Periodic cleanup every 2 minutes
    this.cleanupTimer = setInterval(() => this.evictExpired(), 120_000);
  }

  set<T>(key: string, data: T, ttlMs: number): void {
    // Evict if at capacity (remove the oldest entry)
    if (!this.cache.has(key) && this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + ttlMs
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }

  clear(keyPattern?: string): void {
    if (!keyPattern) {
      this.cache.clear();
      return;
    }
    for (const key of this.cache.keys()) {
      if (key.includes(keyPattern)) {
        this.cache.delete(key);
      }
    }
  }

  size(): number {
    return this.cache.size;
  }

  private evictExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache) {
      if (now > entry.expiresAt) this.cache.delete(key);
    }
  }
}

const cache = new SimpleCache();

/**
 * Invalidate cache entries by key or pattern
 * @param keyPattern - Exact key or pattern to match (undefined clears all)
 */
export function invalidateCache(keyPattern?: string): void {
  cache.clear(keyPattern);
  console.log(`[Cache] Invalidated: ${keyPattern || 'ALL'}`);
}

export function getCachedOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number
): Promise<T> {
  // Check cache first
  const cached = cache.get<T>(key);
  if (cached !== null) {
    console.log(`[Cache] Hit: ${key}`);
    return Promise.resolve(cached);
  }

  // Fetch and cache
  console.log(`[Cache] Miss: ${key}, fetching...`);
  return fetcher().then(data => {
    cache.set(key, data, ttlMs);
    return data;
  });
}

export function clearCache(keyPattern?: string): void {
  cache.clear(keyPattern);
}

// ==================== REQUEST DEDUPLICATION ====================

const pendingRequests = new Map<string, Promise<any>>();

/**
 * Deduplicate concurrent requests with same key
 * If multiple requests with same key are made simultaneously,
 * only execute once and share the result
 */
export function withDeduplication<T>(
  key: string,
  fn: () => Promise<T>
): Promise<T> {
  // Check if request is already pending
  if (pendingRequests.has(key)) {
    console.log(`[Deduplication] Using existing request: ${key}`);
    return pendingRequests.get(key)!;
  }

  // Execute request
  console.log(`[Deduplication] New request: ${key}`);
  const promise = fn().finally(() => {
    // Clean up after completion
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, promise);
  return promise;
}

// ==================== COMBINED UTILITIES ====================

export interface RobustRequestOptions extends RetryOptions, TimeoutOptions {
  circuitBreakerName?: string;
  circuitBreakerOptions?: CircuitBreakerOptions;
  cacheKey?: string;
  cacheTtlMs?: number;
  deduplicationKey?: string;
}

/**
 * Execute a request with all robustness features:
 * - Retry logic with exponential backoff
 * - Timeout protection
 * - Circuit breaker pattern
 * - Response caching
 * - Request deduplication
 */
export async function robustRequest<T>(
  fn: () => Promise<T>,
  options: RobustRequestOptions
): Promise<T> {
  const {
    circuitBreakerName,
    circuitBreakerOptions,
    cacheKey,
    cacheTtlMs,
    deduplicationKey,
    timeoutMs,
    maxAttempts,
    delayMs,
    backoffMultiplier,
    maxDelayMs,
    onRetry,
    timeoutError
  } = options;

  // Build the request pipeline
  let request = fn;

  // 1. Wrap with circuit breaker
  if (circuitBreakerName) {
    const breaker = getCircuitBreaker(circuitBreakerName, circuitBreakerOptions);
    const originalRequest = request;
    request = () => breaker.execute(originalRequest);
  }

  // 2. Wrap with timeout
  const originalRequest = request;
  request = () => withTimeout(originalRequest(), { timeoutMs, timeoutError });

  // 3. Wrap with retry
  const retryWrapped = () => withRetry(request, {
    maxAttempts,
    delayMs,
    backoffMultiplier,
    maxDelayMs,
    onRetry
  });

  // 4. Wrap with deduplication if key provided
  let finalRequest: () => Promise<T>;
  if (deduplicationKey) {
    finalRequest = () => withDeduplication(deduplicationKey, retryWrapped);
  } else {
    finalRequest = retryWrapped;
  }

  // 5. Use cache if provided
  if (cacheKey && cacheTtlMs) {
    return getCachedOrFetch(cacheKey, finalRequest, cacheTtlMs);
  }

  // Execute request
  return finalRequest();
}

// ==================== HEALTH CHECK ====================

export interface HealthCheckResult {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTimeMs: number;
  error?: string;
  timestamp: number;
}

export async function healthCheck(
  serviceName: string,
  checkFn: () => Promise<void>
): Promise<HealthCheckResult> {
  const start = Date.now();
  const result: HealthCheckResult = {
    service: serviceName,
    status: 'healthy',
    responseTimeMs: 0,
    timestamp: Date.now()
  };

  try {
    await withTimeout(checkFn(), { timeoutMs: 5000, timeoutError: 'Health check timeout' });
    result.responseTimeMs = Date.now() - start;

    // Classify based on response time
    if (result.responseTimeMs > 3000) {
      result.status = 'degraded';
    }
  } catch (error: any) {
    result.status = 'down';
    result.error = error.message;
    result.responseTimeMs = Date.now() - start;
  }

  return result;
}

// ==================== EXPORTS ====================

export {
  CircuitState,
  type CircuitBreaker
};
